import { AuthStatusMessage } from '@Auth/components/AuthStatusMessage';
import { ErrorMessage } from '@Pages/pageRuntime/components/ui/ErrorMessage';
import { useTwoFactorChallenge } from '@Pages/pageRuntime/hooks/useTwoFactorChallenge';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { apiErrorMessage } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkTwoFactorChallengeHandle,
  SdkTwoFactorChallengeState
} from '@Pages/pageRuntime/sdk/types';
import type React from 'react';
import { useState } from 'react';

import { TwoFactorBackupCodes } from './TwoFactorBackupCodes';
import {
  TwoFactorFactorForm,
  TwoFactorLinkButton,
  TwoFactorLoadError,
  TwoFactorLoading,
  TwoFactorRestart
} from './TwoFactorPanels';
import { TwoFactorSendCodeButton, TwoFactorSmsConsent } from './TwoFactorSendControls';

type TwoFactorChallengeProps = {
  /**
   * The authenticatable resource. Defaults to the resource of the pending sign-in, then
   * the workspace's sole authenticatable resource.
   */
  resourceId?: string;

  /** Label of the code input. */
  codeLabel?: React.ReactNode;

  /** Contents of the verify buttons. */
  verifyLabel?: React.ReactNode;

  /** Called after the visitor chose to start over and the pending sign-in ended. */
  onCancel?: () => void;

  /** Additional classes for the container. */
  className?: string;

  /** Additional classes for the main buttons. */
  buttonClassName?: string;
};

/**
 * The second step of a sign-in on this page, for a resource that enables two-factor:
 * SMS consent when the number has none, the code sent to the MFA number with a resend
 * countdown, or one of the account's backup codes. A passed code continues to the sign-in
 * destination. It renders nothing unless `Sdk.auth.twoFactor` holds a pending sign-in in
 * the `challenge` stage, so it can sit beside a custom form that calls
 * `Sdk.auth.login({ ..., twoFactor: 'inline' })`. `Sdk.Components.LoginForm` already
 * renders it.
 *
 * @example
 * <Sdk.Components.TwoFactorChallenge />
 */
export function TwoFactorChallenge({
  resourceId,
  codeLabel = 'Verification code',
  verifyLabel = 'Verify',
  onCancel,
  className,
  buttonClassName
}: TwoFactorChallengeProps) {
  const challenge = useTwoFactorChallenge({ resourceId });
  const [usesBackupCode, setUsesBackupCode] = useState(false);

  if (!challenge.isActive) {
    return null;
  }

  if (challenge.needsBackupCodes) {
    return <TwoFactorBackupCodes resourceId={resourceId} className={className} />;
  }

  const cancel = () => {
    void challenge.cancel().then(onCancel);
  };

  if (challenge.restartRequired) {
    return (
      <TwoFactorRestart
        error={challenge.actionError ?? challenge.error}
        fallback="Your sign-in expired. Please sign in again."
        label="Sign in again"
        onRestart={cancel}
        disabled={challenge.isPending}
        className={className}
        buttonClassName={buttonClassName}
      />
    );
  }

  if (challenge.error) {
    return (
      <TwoFactorLoadError
        error={challenge.error}
        fallback="The verification step did not load. Reload the page."
        className={className}
      />
    );
  }

  if (challenge.data === undefined) {
    return <TwoFactorLoading label="Loading the verification step" className={className} />;
  }

  const links = (
    <div className="flex items-center justify-between">
      <TwoFactorLinkButton onClick={() => setUsesBackupCode((current) => !current)}>
        {usesBackupCode ? 'Use a text message code instead' : 'Use a backup code instead'}
      </TwoFactorLinkButton>
      <TwoFactorLinkButton onClick={cancel} disabled={challenge.isPending}>
        Start over
      </TwoFactorLinkButton>
    </div>
  );

  return (
    <div className={cn('grid gap-3', className)}>
      {usesBackupCode ? (
        <BackupCodeStep
          challenge={challenge}
          verifyLabel={verifyLabel}
          buttonClassName={buttonClassName}
        />
      ) : challenge.data.step === 'consent' ? (
        <>
          <p className="text-sm text-muted-foreground">
            Your account needs a verification code to finish signing in.
          </p>
          <TwoFactorSmsConsent
            flow="challenge"
            resourceId={resourceId}
            buttonClassName={buttonClassName}
          />
        </>
      ) : (
        <CodeStep
          challenge={challenge}
          state={challenge.data}
          resourceId={resourceId}
          codeLabel={codeLabel}
          verifyLabel={verifyLabel}
          buttonClassName={buttonClassName}
        />
      )}
      {links}
    </div>
  );
}

function actionErrors(challenge: SdkTwoFactorChallengeHandle): string[] {
  return challenge.actionError
    ? [apiErrorMessage(challenge.actionError, 'The code did not verify.')]
    : [];
}

function BackupCodeStep({
  challenge,
  verifyLabel,
  buttonClassName
}: {
  challenge: SdkTwoFactorChallengeHandle;
  verifyLabel: React.ReactNode;
  buttonClassName?: string;
}) {
  const [backupCode, setBackupCode] = useState('');

  return (
    <>
      <p className="text-sm text-muted-foreground">
        Enter one of the backup codes that you saved. Each code works only once.
      </p>
      <TwoFactorFactorForm
        usesBackupCode
        value={backupCode}
        onChange={setBackupCode}
        onSubmit={(value) =>
          void challenge.verifyBackupCode(value).then((verified) => verified || setBackupCode(''))
        }
        submitLabel={verifyLabel}
        errors={actionErrors(challenge)}
        disabled={challenge.isPending}
        buttonClassName={buttonClassName}
      />
    </>
  );
}

function CodeStep({
  challenge,
  state,
  resourceId,
  codeLabel,
  verifyLabel,
  buttonClassName
}: {
  challenge: SdkTwoFactorChallengeHandle;
  state: SdkTwoFactorChallengeState;
  resourceId?: string;
  codeLabel: React.ReactNode;
  verifyLabel: React.ReactNode;
  buttonClassName?: string;
}) {
  const [code, setCode] = useState('');

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {state.deliveryError
          ? `We could not text a code to the phone number ending ${state.maskedPhone}. Request a new code or use a backup code.`
          : `We sent a 6-digit code to the phone number ending ${state.maskedPhone}.`}
      </p>
      {state.status ? (
        <AuthStatusMessage title="Code sent">{state.status}</AuthStatusMessage>
      ) : null}
      <ErrorMessage errors={state.deliveryError ? [state.deliveryError] : []} />
      <TwoFactorFactorForm
        usesBackupCode={false}
        value={code}
        onChange={setCode}
        onSubmit={(value) =>
          void challenge.verifyCode(value).then((verified) => verified || setCode(''))
        }
        submitOnComplete
        codeLabel={codeLabel}
        submitLabel={verifyLabel}
        errors={actionErrors(challenge)}
        disabled={challenge.isPending}
        buttonClassName={buttonClassName}
      />
      <TwoFactorSendCodeButton
        flow="challenge"
        resourceId={resourceId}
        label="Send a new code"
        variant="link"
      />
    </>
  );
}
